import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useMemo } from 'react';

import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../../providers/portfolio-management';
import { StrikeReportContext } from '../../../../../../providers/strike-report';
import { IStrikeReportData } from '../../../../../../providers/strike-report/strike-report.types';
import { CheckLastUpdate } from '../../../../components/check-last-update';
import { getStrikeReportColumns } from '../../helpers/strike-report-data-columns';
import { getStrikeReportRows } from '../../helpers/strike-report-data-rows';
import { filterEmptyExpiries, filterEmptyStrikes } from '../../helpers/strike-report-filters';
import { strikeReportFlattenColumns } from '../../helpers/strike-report-flatten-columns';
import { strikeReportRenderRows } from '../../helpers/strike-report-render-rows';

import './styles.scss';

type StrikeReportTableProps = {
  reportData: IStrikeReportData | null;
};

export const StrikeReportTable = ({ reportData }: StrikeReportTableProps) => {
  const { selectedDataDisplayOption, selectedAccountOption } = useContext(
    PortfolioManagementContext,
  );
  const {
    strikeReportExpandedCols,
    strikeReportExpandedRows,
    strikeReportToggleColumn,
    strikeReportToggleRow,
    strikeReportFilters: { strikeReportFilterDisplay },
    sortStrikeAsc,
    setSortStrikeAsc,
  } = useContext(StrikeReportContext);

  const strikeReportColumns = getStrikeReportColumns({
    data: reportData?.data,
    hasNoChildren:
      selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount &&
      selectedAccountOption.exchange !== 'otc',
  });
  const strikeReportRows = getStrikeReportRows(reportData?.data);

  const displayedColumns = filterEmptyExpiries(
    strikeReportFlattenColumns(strikeReportColumns, strikeReportExpandedCols),
    strikeReportRows,
    strikeReportFilterDisplay,
  );
  const filteredRows = filterEmptyStrikes(strikeReportRows, strikeReportFilterDisplay);

  const sortedRows = useMemo(() => {
    const rowsCopy = [...filteredRows];
    rowsCopy.sort((a, b) =>
      sortStrikeAsc ? Number(a.label) - Number(b.label) : Number(b.label) - Number(a.label),
    );
    return rowsCopy;
  }, [filteredRows, sortStrikeAsc]);

  const renderStrikeReportTable = useMemo(() => {
    return (
      <div className="strike-report-table-container">
        <CheckLastUpdate lastUpdate={reportData?.lastUpdate as string} />

        <div className="table-wrapper">
          <table>
            <thead>
              <th className="sticky-col" onClick={() => setSortStrikeAsc(!sortStrikeAsc)}>
                <span>Strike</span>
                <FontAwesomeIcon
                  icon={sortStrikeAsc ? faArrowDown : faArrowUp}
                  className="col-toggle-icon"
                />
              </th>

              {displayedColumns.map((col: any) => {
                const hasChildren = col.children && col.children.length > 0;
                const isExpanded = strikeReportExpandedCols.includes(col.id);

                return (
                  <th
                    key={col.id}
                    onClick={() => hasChildren && strikeReportToggleColumn(col.id)}
                    className={`column-level-${col.level} ${hasChildren ? 'expandable-col' : ''}`}
                    style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                  >
                    <div className="expandable-col-content">
                      {col.icon && (
                        <img src={col.icon} className="col-level-account-icon" alt={col.label} />
                      )}
                      <span> {col.label}</span>
                      {hasChildren && (
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronLeft : faChevronRight}
                          className="col-toggle-icon"
                          style={{ marginLeft: 4 }}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </thead>

            <tbody>
              {strikeReportRenderRows({
                nodes: sortedRows,
                displayedColumns,
                expandedRows: strikeReportExpandedRows,
                toggleRow: strikeReportToggleRow,
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [
    reportData,
    strikeReportExpandedRows,
    strikeReportExpandedCols,
    strikeReportFilterDisplay,
    sortStrikeAsc,
  ]);

  return renderStrikeReportTable;
};
